import React, { useCallback } from 'react'

// Utils
import { dataIdFromObject } from '@sketch/graphql-cache'
import { addProjectMembership } from '../../../operations/utils/projectMemberships.cache'

// Components
import { Flex, Text, InfiniteList, handleFetchMore } from '@sketch/components'
import { AddMemberForm } from 'modules/projects/modals/ProjectSharingModal/ProjectSharingForm'
import { GuestMemberRow } from './GuestMemberRow'
import { FormValues } from '../../AddNewProjectModal/useAutocomplete'

// Styles
import {
  EmptyTextContainer,
  PersonIconStyled as PersonIcon,
} from './GuestsPanel.styles'

// GraphQL
import {
  WorkspaceMinimalFragment,
  useAddProjectMemberMutation,
  ProjectInSidebarAndHeaderFragment,
  ProjectMembershipFragment,
  WorkspaceMembershipFragment,
} from '@sketch/gql-types'

interface GuestsPanelProps {
  project: ProjectInSidebarAndHeaderFragment
  workspace: WorkspaceMinimalFragment
  guests: ProjectMembershipFragment[]
  workspaceMemberships: WorkspaceMembershipFragment[]
  after?: string | null
  fetchMore: any
  editable?: boolean
}

export const GuestsPanel: React.FC<GuestsPanelProps> = ({
  project,
  workspace,
  guests,
  workspaceMemberships,
  after,
  fetchMore,
  editable = true,
}) => {
  const [addMemberToProject] = useAddProjectMemberMutation({
    onError: 'show-toast',
    update(cache, response) {
      if (!response.data?.addProjectMember.projectMembership) {
        return
      }

      addProjectMembership({
        cache,
        projectIdentifier: project.identifier,
        membership: response.data?.addProjectMember.projectMembership,
      })
    },
  })

  const handleAddMember = useCallback(
    async (values: FormValues) => {
      const variables = {
        projectIdentifier: project.identifier,
        email: values.email || undefined,
        workspaceMembershipIdentifier:
          values.workspaceMembershipIdentifier || undefined,
        guestCommentsEnabled: values.guestCommentsEnabled,
        guestInspectEnabled: values.guestInspectEnabled,
      }

      await addMemberToProject({ variables })
    },
    [addMemberToProject, project.identifier]
  )

  return (
    <>
      <AddMemberForm
        workspace={workspace}
        projectIdentifier={project.identifier}
        membersList={workspaceMemberships}
        onMemberAdd={handleAddMember}
        hideLabel
        filterMembers="Guests"
        removeSelf
      />
      {guests.length === 0 ? (
        <Flex justifyContent="center" alignItems="center">
          <EmptyTextContainer>
            <PersonIcon height={64} width={64} />
            <Text textStyle="copy.primary.standard.E" pt={3}>
              No Guests yet
            </Text>
            <Text textStyle="copy.quaternary.standard.D">
              Enter a name or email to invite somebody as a Guest.
            </Text>
          </EmptyTextContainer>
        </Flex>
      ) : (
        <InfiniteList
          canLoadMore={after !== null}
          onLoadMore={handleFetchMore(
            fetchMore,
            ['project', 'guests', 'entries'],
            {
              dataIdFromObject,
              after,
            }
          )}
        >
          {guests.map(member => (
            <GuestMemberRow
              key={member.identifier}
              member={member as ProjectMembershipFragment}
              project={project}
              editable={editable}
            />
          ))}
        </InfiniteList>
      )}
    </>
  )
}
